import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Copy } from 'lucide-react'
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { Button } from 'tailwind-v4/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'tailwind-v4/components/field'
import { Input } from 'tailwind-v4/components/input'
import {
  NativeSelect,
  NativeSelectOption,
} from 'tailwind-v4/components/native-select'
import { Textarea } from 'tailwind-v4/components/textarea'
import { z } from 'zod'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import {
  RoleDisplayNames,
  RoleSortOrder,
  type UserRoleType,
} from '@/state/auth/auth.types'
import useUser, { useUserRole } from '@/hooks/useUser'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import { hookFormErrorToast } from '@/helpers/zod'
import config from '@/config'
import LocationSelect from '@/common/Form/LocationSelect'
import { isAxiosError } from '@/utils/axiosHelper'

const roleValues = [
  'admin',
  'moderator',
  'gm',
  'reports',
  'booking',
  'marketeer',
  'front-of-house',
  'it-support',
] as const

type InviteRole = (typeof roleValues)[number]

const inviteSchema = z.object({
  serials: z.array(z.string().length(12)),
  email: z.string().email(),
  success_link: z.string().nullable(),
  role: z.enum(roleValues),
})

type InviteFormValues = z.infer<typeof inviteSchema>

function extractInviteErrorMessage(error: unknown): string {
  if (
    isAxiosError<{ message?: string }>(error) &&
    typeof error.response?.data?.message === 'string'
  ) {
    return error.response.data.message
  }
  return 'Something went wrong sending this invite. Please try again.'
}

function toSerialList(value: string | string[]): string[] {
  if (Array.isArray(value)) {
    return value
  }
  return value ? [value] : []
}

interface UserInviteContextValue {
  isOpen: boolean
  onClose: () => void
  onOpen: (options: { role?: string }) => void
  onOpenResent: (email: string) => void
}

const initialState: UserInviteContextValue = {
  isOpen: false,
  onClose: () => {},
  onOpen: () => {},
  onOpenResent: () => {},
}

const UserInviteContext = createContext(initialState)

export function UserInviteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null)
  const [wasResend, setWasResend] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [messageCopied, setMessageCopied] = useState(false)
  const { org_id } = useParams<{ org_id: string }>()
  const [search] = useSearchParams()
  const { data: roles = [] } = morpheusApi.useGetOrganisationUserRolesQuery(
    {
      orgId: org_id ?? '',
    },
    {
      skip: !isOpen,
    }
  )
  const { serial } = useParams<{ serial: string }>()
  const { pathname } = useLocation()
  const initialValues = useMemo<InviteFormValues>(
    () => ({
      serials: serial ? [serial] : [],
      email: '',
      success_link: pathname.includes('organisation/team')
        ? null
        : `${config.url.connect}${pathname}${search.toString() ? `?${search.toString()}` : ''}`,
      role: 'admin',
    }),
    [serial, pathname, search]
  )

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValid },
    reset,
    setValue,
    watch,
  } = useForm<InviteFormValues>({
    mode: 'onChange',
    delayError: 300,
    defaultValues: initialValues,
    resolver: zodResolver(inviteSchema),
  })

  useEffect(() => {
    reset(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on mount, not on every route/search change
  }, [])

  const [invite, { isLoading }] = morpheusApi.useInviteMutation()
  const org = useSelectedOrganisation()

  const closeAndReset = () => {
    setIsOpen(false)
    setInvitedEmail(null)
    setWasResend(false)
    setSubmitError(null)
    setMessageCopied(false)
  }

  const onSubmit = () => {
    if (!org_id) {
      console.error('Organisation ID is not defined')
      return
    }

    setSubmitError(null)
    const values = getValues()
    return invite({ orgId: org_id, data: values })
      .unwrap()
      .then(() => {
        reset(initialValues)
        setInvitedEmail(values.email)
      })
      .catch((error: unknown) => {
        setSubmitError(extractInviteErrorMessage(error))
      })
  }

  const serials = watch('serials')
  const email = watch('email')
  const user = useUser()
  const role = useUserRole()

  useEffect(() => {
    if (!messageCopied) return undefined

    const timer = setTimeout(() => {
      setMessageCopied(false)
    }, 2000)

    return () => {
      clearTimeout(timer)
    }
  }, [messageCopied])

  const shareMessage = invitedEmail
    ? `Hi there,\n\nI've invited you to join ${org?.name ?? 'my organisation'} on Stampede.\n\nSign in or create an account using ${invitedEmail} here to accept:\n${config.url.connect}`
    : ''

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage)
      setMessageCopied(true)
    } catch (err) {
      console.error('Failed to copy invite message:', err)
    }
  }

  const assignableRoles = useMemo(() => {
    if (!role) {
      return []
    }
    return roles
      .filter((item: UserRoleType) => {
        if (role.legacy_id === 0 || role.legacy_id === 1) {
          return item.legacy_id >= 2
        }
        return item.legacy_id >= role.legacy_id
      })
      .sort(
        (a: UserRoleType, b: UserRoleType) =>
          (RoleSortOrder[a.key] ?? 99) - (RoleSortOrder[b.key] ?? 99)
      )
  }, [roles, role])

  let dialogBody: ReactNode = null
  if (invitedEmail) {
    dialogBody = (
      <>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-success/10">
            <Check className="size-6 text-success" />
          </div>
          <DialogTitle>
            {wasResend ? 'Invite resent' : 'Invite sent successfully'}
          </DialogTitle>
          <DialogDescription>
            {wasResend ? "We've resent an invite to" : "We've emailed"}{' '}
            <strong className="text-foreground">{invitedEmail}</strong> a link
            to join the team. If the email doesn&apos;t arrive (it can end up in
            spam), they can sign in or create an account directly at
            product.stampede.ai using this email address — no need to wait for
            it.
          </DialogDescription>
        </div>

        <Field>
          <FieldLabel htmlFor="invite_share_message">
            Message to share
          </FieldLabel>
          <Textarea
            id="invite_share_message"
            readOnly
            rows={6}
            value={shareMessage}
            onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
              e.target.select()
            }}
          />
          <FieldDescription>
            Share this if they haven&apos;t received the invite email.
          </FieldDescription>
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="font-medium"
              leftIcon={
                messageCopied ? <Check className="text-success" /> : <Copy />
              }
              onClick={handleCopyMessage}
            >
              {messageCopied ? 'Copied' : 'Copy message'}
            </Button>
          </div>
        </Field>

        <DialogFooter>
          <Button variant="primary" onClick={closeAndReset}>
            Done
          </Button>
        </DialogFooter>
      </>
    )
  } else if (user) {
    dialogBody = (
      <form
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit, hookFormErrorToast)}
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="guest_invite">Invite email</FieldLabel>
            <Input
              type="email"
              id="guest_invite"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setValue('email', e.target.value)
              }}
            />
          </Field>

          {role ? (
            <Field>
              <FieldLabel htmlFor="role">Role</FieldLabel>
              <NativeSelect
                id="role"
                className="w-full"
                value={watch('role')}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setValue('role', e.target.value as InviteRole)
                }}
              >
                {assignableRoles.map((item) => (
                  <NativeSelectOption key={item.key} value={item.key}>
                    {RoleDisplayNames[item.name] ?? item.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}

          <Field>
            <FieldLabel>Access to venues</FieldLabel>
            <LocationSelect
              resetText="Can access all locations"
              size="md"
              mode="multiple"
              value={serials}
              onChange={(v: string | string[]) => {
                const list = toSerialList(v)
                const hasNullValue = list.filter((item) => !item)
                setValue('serials', hasNullValue.length >= 1 ? [] : list)
              }}
            />
          </Field>

          {isValid ? (
            <FieldDescription>
              An email will be sent to{' '}
              <strong className="text-foreground">{email}</strong> with a link
              to create their account. Once they complete this step we&apos;ll
              email you at{' '}
              <strong className="text-foreground">{user.email}</strong> to let
              you know.
            </FieldDescription>
          ) : null}

          {submitError ? <FieldError>{submitError}</FieldError> : null}
        </FieldGroup>

        <DialogFooter>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting || isLoading ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogFooter>
      </form>
    )
  }

  return (
    <UserInviteContext.Provider
      value={{
        isOpen,
        onClose: closeAndReset,
        onOpen: ({ role: roleParam }: { role?: string }) => {
          setTimeout(() => {
            setValue('role', (roleParam ?? 'admin') as InviteRole)
          }, 1000)
          setIsOpen(true)
        },
        onOpenResent: (email: string) => {
          setInvitedEmail(email)
          setWasResend(true)
          setIsOpen(true)
        },
      }}
    >
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsOpen(true)
            return
          }
          closeAndReset()
        }}
      >
        <DialogContent className="sm:max-w-lg">{dialogBody}</DialogContent>
      </Dialog>

      {children}
    </UserInviteContext.Provider>
  )
}

export const useUserInvite = () => {
  return useContext(UserInviteContext)
}
